import React from "react";
import { X, Printer, FileText } from "lucide-react";

const money = (v) => `${v >= 0 ? "+" : ""}${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtDate = (s) => { if (!s) return "—"; try { return new Date(s + "T00:00:00Z").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }); } catch { return s; } };

const Row = ({ label, value, tone }) => (
  <div className="flex justify-between py-1.5 border-b border-[color:var(--ws-border)] text-[13px]">
    <span className="text-[color:var(--ws-muted)]">{label}</span>
    <span className={`font-semibold ${tone === "up" ? "ws-up" : tone === "down" ? "ws-down" : "text-white"}`}>{value}</span>
  </div>
);

const Section = ({ title, children }) => (
  <div className="mb-5">
    <h4 className="ws-heading text-[13px] font-bold uppercase tracking-[0.16em] text-[color:var(--ws-cyan)] mb-2">{title}</h4>
    {children}
  </div>
);

const TradingReport = ({ real, rangeLabel, onClose }) => {
  if (!real || !real.has_data) return null;
  const t = real.totals || {};
  const adv = real.advanced || {};
  const k = adv.kpis || {};
  const st = adv.streaks || {};
  const rng = real.range || {};
  const pf = t.profit_factor == null ? "—" : t.profit_factor >= 999 ? "∞" : t.profit_factor.toFixed(2);

  const days = (real.by_day || []).filter((d) => d.trades > 0);
  const bestDay = days.length ? days.reduce((a, b) => (b.pnl > a.pnl ? b : a)) : null;
  const worstDay = days.length ? days.reduce((a, b) => (b.pnl < a.pnl ? b : a)) : null;
  const syms = real.by_symbol || [];
  const sessions = (adv.sessions || []).filter((s) => s.trades > 0);
  const eqStart = (real.equity_daily || [])[0];
  const eqEnd = (real.equity_daily || [])[real.equity_daily?.length - 1];

  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto p-4" style={{ background: "rgba(3,7,18,0.8)", backdropFilter: "blur(6px)" }} data-testid="trading-report-modal">
      <div className="ws-report-print ws-panel w-full max-w-[760px] my-8 p-8 relative" style={{ background: "var(--ws-bg-elev, #0a1120)" }}>
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="ws-icon-chip w-10 h-10"><FileText className="w-5 h-5" /></div>
            <div>
              <h2 className="ws-heading text-[24px] font-bold ws-title-gradient leading-tight">Trading Report</h2>
              <p className="text-[12px] text-[color:var(--ws-muted)]">
                {rangeLabel} · {fmtDate(rng.first_date)} → {fmtDate(rng.last_date)} · generated {new Date().toLocaleDateString()}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 ws-report-hide">
            <button onClick={() => window.print()} className="ws-btn-primary px-3 py-2 rounded-lg text-[12px] font-semibold inline-flex items-center gap-2" data-testid="report-print-btn">
              <Printer className="w-3.5 h-3.5" /> Print / Save PDF
            </button>
            <button onClick={onClose} className="ws-btn-ghost p-2 rounded-lg" data-testid="report-close-btn"><X className="w-4 h-4" /></button>
          </div>
        </div>

        <Section title="Headline P&L">
          <Row label="Net realized P&L" value={money(t.net_profit)} tone={t.net_profit >= 0 ? "up" : "down"} />
          <Row label="Total closed trades" value={t.total_trades} />
          <Row label="Win rate" value={`${t.win_rate}% (${t.wins}W / ${t.losses}L)`} />
          <Row label="Gross profit / loss" value={`${money(t.gross_profit)} / ${money(-Math.abs(t.gross_loss))}`} />
        </Section>

        <Section title="Key Metrics">
          <div className="grid grid-cols-2 gap-x-6">
            <Row label="Profit factor" value={pf} tone={t.profit_factor >= 1 ? "up" : "down"} />
            <Row label="Expectancy / trade" value={money(k.expectancy)} tone={k.expectancy >= 0 ? "up" : "down"} />
            <Row label="Avg win" value={money(t.avg_win)} tone="up" />
            <Row label="Avg loss" value={money(t.avg_loss)} tone="down" />
            <Row label="Best trade" value={money(k.best_trade)} tone="up" />
            <Row label="Worst trade" value={money(k.worst_trade)} tone="down" />
            <Row label="Max drawdown" value={`${money(-Math.abs(k.max_drawdown))} (${k.max_drawdown_pct}%)`} tone="down" />
            <Row label="Behavioral score" value={`${real.behavioral?.score ?? "—"}/100`} />
          </div>
        </Section>

        <Section title="Equity Summary">
          <Row label="Start → End cumulative" value={`${money(eqStart?.day_pnl || 0)} → ${money(eqEnd?.cumulative || 0)}`} tone={(eqEnd?.cumulative || 0) >= 0 ? "up" : "down"} />
          <Row label="Trading days" value={real.equity_daily?.length || 0} />
        </Section>

        <div className="grid md:grid-cols-2 gap-x-8">
          <Section title="Best / Worst Day">
            {bestDay && <Row label={`Best: ${bestDay.day}`} value={money(bestDay.pnl)} tone="up" />}
            {worstDay && <Row label={`Worst: ${worstDay.day}`} value={money(worstDay.pnl)} tone="down" />}
          </Section>
          <Section title="Streaks">
            <Row label="Current streak" value={`${st.current || 0}${st.current_type === "win" ? "W" : st.current_type === "loss" ? "L" : ""}`} tone={st.current_type === "win" ? "up" : st.current_type === "loss" ? "down" : undefined} />
            <Row label="Longest win / loss" value={`${st.longest_win || 0} / ${st.longest_loss || 0}`} />
          </Section>
        </div>

        <Section title="Top Symbols">
          {syms.slice(0, 5).map((s) => (
            <Row key={s.symbol} label={`${s.symbol} · ${s.trades} trades · ${s.win}% win`} value={money(s.pnl)} tone={s.pnl >= 0 ? "up" : "down"} />
          ))}
          {syms.length === 0 && <div className="text-[12px] text-[color:var(--ws-muted)]">No symbols in range.</div>}
        </Section>

        <Section title="Sessions (UTC)">
          {sessions.map((s) => (
            <Row key={s.name} label={`${s.name} · ${s.trades} trades · ${s.win}% win`} value={money(s.pnl)} tone={s.pnl >= 0 ? "up" : "down"} />
          ))}
          {sessions.length === 0 && <div className="text-[12px] text-[color:var(--ws-muted)]">No session data.</div>}
        </Section>

        <Section title="Key Patterns & AI Insights">
          <div className="space-y-2">
            {(adv.insights || []).map((ins, i) => (
              <div key={i} className="text-[12px]">
                <span className="font-semibold text-white">{ins.title}</span>
                <span className="text-[color:var(--ws-muted)]"> · {ins.confidence} confidence · n={ins.sample_size}</span>
                <div className="text-[color:var(--ws-muted)] leading-snug">{ins.evidence} <span className="text-[color:var(--ws-text)]">→ {ins.recommendation}</span></div>
              </div>
            ))}
          </div>
        </Section>

        <p className="text-[10px] text-[color:var(--ws-muted)] mt-4 pt-3 border-t border-[color:var(--ws-border)]">
          All figures are computed from your real synced MT5 closed trades (net = profit + commission + swap + fees). No estimated or fabricated values.
        </p>
      </div>
    </div>
  );
};

export default TradingReport;
